/**
 * Визуальные признаки папок Диска (defineDriveDecorations).
 *
 * Модуль или инстанс регистрирует правило «эта папка особенная → вот как её
 * пометить»: акцент, значок, бейдж. Диск сам решает, ГДЕ и ЧЕМ это рисовать
 * (дерево — полоска слева и значок у имени, список — то же у имени),
 * разработчик описывает только применимость и семантику.
 *
 *   defineDriveDecorations({
 *     when: folder => /^mws:week:\d+$/.test(folder.code ?? ''),
 *     decorate: folder =>
 *       isCurrentWeekFolder(folder.name)
 *         ? { accent: 'green', icon: 'check', tooltip: 'Текущая отчётная неделя' }
 *         : null,
 *   });
 *
 * Почему отдельный реестр, а не поле в `defineDriveActions`: тот отвечает на
 * вопрос «что здесь можно сделать», этот — «как это выглядит». Декорации нужны
 * и там, где действий нет, а регистрировать пустой набор действий ради
 * подсветки было бы странно.
 *
 * **Словарь декораций закрытый** (`accent`/`icon`/`badge`/`tooltip`),
 * произвольные стили и JSX
 * не принимаются. Причина: «эта неделя — текущая» знает инстанс, а «текущая =
 * полоска такого-то цвета слева от строки дерева, бейдж после имени, до
 * ховер-меню» — знание о вёрстке Диска, которого у инстанса нет. Одна
 * декорация рисуется в НЕСКОЛЬКИХ поверхностях (дерево, список, плитка), и
 * семантический токен каждая отрисует по-своему правильно; захардкоженный
 * инстансом стиль осмыслен ровно в одной. Нужен новый признак — поле в словарь
 * добавляет платформа, и оно сразу работает во всех поверхностях.
 *
 * Регистрация — в `client/bootstrap.ts` модуля/инстанса, один раз до рендера.
 */
import type { Folder } from '../../schemas/folder.schema';

/**
 * Значок-признак папки. Список закрытый: инстанс выбирает СМЫСЛ, конкретную
 * иконку подбирает платформа (сейчас `check` — `IconCircleCheckFilled`).
 * Новое значение добавляется сюда же и сразу работает во всех поверхностях.
 */
export type DriveDecorationIcon = 'check';

/**
 * Визуальная декорация папки. Словарь закрытый — расширяется ПЛАТФОРМОЙ
 * (см. заголовок модуля), инстанс произвольную разметку не задаёт.
 */
export interface DriveFolderDecoration {
  /**
   * Акцент папки — токен палитры Mantine (`blue`, `teal`, `orange`…).
   * Дерево рисует им полоску слева от строки, список — цвет бейджа.
   * Намеренно НЕ фон строки: фоном отмечена активная папка (состояние
   * навигации), и декорация не должна с ним конкурировать.
   */
  accent?: string;
  /**
   * Короткий бейдж рядом с именем («черновик»). Длинный текст сломает строку —
   * там же живут значок зоны, аватарки и ховер-меню. Если признак умещается в
   * значок, предпочтительнее `icon`: он не двигает имя папки.
   */
  badge?: string;
  /** Значок-признак у имени папки — компактная альтернатива бейджу. */
  icon?: DriveDecorationIcon;
  /**
   * Подпись при наведении на значок/бейдж («Текущая отчётная неделя»).
   * Для `icon` практически обязательна: сама по себе галочка не объясняет,
   * что она означает.
   */
  tooltip?: string;
}

export interface DriveDecorationRule {
  /**
   * «Моя ли это папка». СИНХРОННЫЙ предикат только по полям папки: зовётся на
   * каждый рендер узла дерева и строки списка — запрос в БД тут убил бы
   * отзывчивость. Обычно — проверка якоря `folder.code`.
   */
  when: (folder: Folder) => boolean;
  /**
   * Чем пометить папку; `null` — ничем (правило применимо к папке, но признак
   * не сработал: неделя не текущая, инспекция уже принята).
   *
   * Тоже синхронный и по возможности чистый: результат зависит от полей папки
   * и внешнего состояния вроде текущей даты, но не от запросов.
   */
  decorate: (folder: Folder) => DriveFolderDecoration | null;
}

/** Реестр правил (in-memory). Наполняется модулями/инстансами в их bootstrap.ts. */
const rules: DriveDecorationRule[] = [];

/** Зарегистрировать визуальный признак папок Диска (см. DriveDecorationRule). */
export function defineDriveDecorations(rule: DriveDecorationRule): void {
  rules.push(rule);
}

/**
 * Итоговая декорация папки или `null`, если её никто не пометил.
 *
 * Совпало несколько правил — поля МЕРЖАТСЯ, при конфликте одного поля
 * побеждает ПОСЛЕДНЯЯ регистрация (правило старшинства; без него результат
 * зависел бы от порядка импортов в bootstrap — недокументированная семантика,
 * которую однажды «починят» перестановкой строк, сломав другой инстанс).
 * Так «неделя текущая» (accent + icon) и «в неделе есть непринятые NCR»
 * (badge) от разных правил складываются в одну пометку.
 */
export function getDriveFolderDecoration(
  folder: Folder | null | undefined
): DriveFolderDecoration | null {
  if (!folder) return null;
  let merged: DriveFolderDecoration | null = null;
  for (const rule of rules) {
    if (!rule.when(folder)) continue;
    const decoration = rule.decorate(folder);
    if (!decoration) continue;
    merged = { ...(merged ?? {}), ...decoration };
  }
  return merged;
}

/** Очистить реестр — только для изолированных тестов. */
export function __clearDriveDecorationsForTest(): void {
  rules.length = 0;
}
